import { Loader2, RefreshCw, AlertTriangle, Info } from 'lucide-preact'
import { assetApi, type EmailSubjectVariantDto } from '@/api/asset'
import { useEmailPreviewFetch } from '@/hooks/useEmailPreviewFetch'

interface EmailCopyPreviewProps {
    clientCompanyId: string
    workspaceId: string
    campaignId: string
    assetId: string
    /** Subject shown in the gmail-style header. Live editor value preferred over the
     *  persisted state so the user sees their unsaved typing immediately. */
    subject?: string | null
    /** A/B subject-line challenger variants (ORB-453). When non-empty, the subject bar shows a
     *  two-row A/B view (Subject above is variant A). */
    subjectVariants?: EmailSubjectVariantDto[] | null
    /** Live HTML from Unlayer's `exportHtml`. When provided, the server renders this
     *  directly; otherwise it falls back to the asset's persisted DraftHtml. */
    htmlOverride?: string | null
    /** Re-render whenever this changes (in addition to field changes). Used by the chat
     *  panel to force a refresh after Webster patches the asset. */
    refreshKey?: number
}

/**
 * Live preview of the merged Email Copy HTML, rendered inside a sandboxed iframe (srcdoc)
 * so the email's own styles don't leak into the dashboard.
 *
 * Debounces requests by 300ms before hitting POST /EmailCopyPreview, and uses
 * AbortController to cancel in-flight requests when newer keystrokes arrive.
 */
export function EmailCopyPreview({
    clientCompanyId,
    workspaceId,
    campaignId,
    assetId,
    subject,
    subjectVariants,
    htmlOverride,
    refreshKey = 0,
}: EmailCopyPreviewProps) {
    const { preview, error, loading, refresh } = useEmailPreviewFetch(
        signal => assetApi.previewEmailCopy(
            clientCompanyId, workspaceId, campaignId, assetId,
            htmlOverride ? { HtmlOverride: htmlOverride } : undefined,
            signal,
        ),
        `${clientCompanyId}|${workspaceId}|${campaignId}|${assetId}|${htmlOverride ?? ''}|${refreshKey}`,
        { debounceMs: 300 },
    )

    return (
        <div className="flex flex-col h-full bg-slate-100 dark:bg-slate-950">
            {/* Notices stack — render above the subject bar so they push the iframe down
                rather than overlaying it. */}
            {error && (
                <div className="flex-shrink-0 flex items-start gap-2 px-4 py-2 bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 text-xs text-red-700 dark:text-red-300">
                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                </div>
            )}
            {preview?.Warning && (
                <div className="flex-shrink-0 flex items-start gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300">
                    <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>{preview.Warning}</span>
                </div>
            )}

            {/* Subject bar (gmail-style) */}
            <div className="flex-shrink-0 flex items-center justify-between gap-2 px-4 py-2 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
                {subjectVariants && subjectVariants.length > 0 ? (
                    <div className="min-w-0 flex-1 space-y-1.5">
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300">A</span>
                            <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">Subject</div>
                                <div className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                                    {subject || preview?.Subject || <span className="italic text-slate-400">No subject yet</span>}
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 min-w-0">
                            <span className="flex-shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">B</span>
                            <div className="min-w-0">
                                <div className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">Subject</div>
                                <div className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                                    {subjectVariants[0].Subject || <span className="italic text-slate-400">No subject yet</span>}
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="min-w-0 flex-1">
                        <div className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">Subject</div>
                        <div className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                            {subject || preview?.Subject || <span className="italic text-slate-400">No subject yet</span>}
                        </div>
                    </div>
                )}
                <button
                    type="button"
                    onClick={refresh}
                    title="Refresh preview"
                    className="inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
            </div>

            {/* Preview body */}
            <div className="flex-1 relative overflow-hidden">
                {preview && (
                    <iframe
                        title="Email preview"
                        srcDoc={preview.HtmlBody}
                        sandbox=""
                        className="w-full h-full border-0 bg-white"
                    />
                )}

                {loading && !preview && (
                    <div className="absolute inset-0 flex items-center justify-center">
                        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                    </div>
                )}
            </div>
        </div>
    )
}
